/* BA: header dropdowns.
 *
 * Horizon renders header submenus as a full-width mega menu built on a page
 * grid wrapping a six-column subgrid. Coercing that into a narrow single-column
 * panel with CSS did not hold -- text wrapped, stray backgrounds showed through,
 * and even inline !important on grid-template-columns was ignored.
 *
 * So instead of fighting it, this reads the links Horizon rendered and builds a
 * clean panel with our own markup and class names. Nothing inherits Horizon's
 * mega-menu layout, so nothing has to be overridden.
 *
 * Second-level items that have children collapse behind a chevron and start
 * closed -- one open group at a time.
 */
(function () {
  'use strict';

  var BUILT = 'data-ba-dd';

  function textOf(a) {
    var s = a.querySelector('.wrap-text');
    return (s ? s.textContent : a.textContent).trim();
  }

  /* Read Horizon's rendered submenu into [{title, url, children:[...]}] */
  function harvest(submenu) {
    var groups = [];
    submenu.querySelectorAll('.mega-menu__link--parent').forEach(function (p) {
      var kids = [];
      var list = p.nextElementSibling;
      if (list && list.tagName === 'UL') {
        list.querySelectorAll('a').forEach(function (a) {
          kids.push({ title: textOf(a), url: a.getAttribute('href') });
        });
      }
      groups.push({ title: textOf(p), url: p.getAttribute('href'), children: kids });
    });

    /* Links with no children of their own */
    submenu.querySelectorAll('.mega-menu__link').forEach(function (a) {
      if (a.classList.contains('mega-menu__link--parent')) return;
      if (a.closest('ul') && a.closest('ul').previousElementSibling
          && a.closest('ul').previousElementSibling.classList
          && a.closest('ul').previousElementSibling.classList.contains('mega-menu__link--parent')) return;
      groups.push({ title: textOf(a), url: a.getAttribute('href'), children: [] });
    });

    return groups.filter(function (g) { return g.title; });
  }

  function build(groups) {
    var wrap = document.createElement('div');
    wrap.className = 'ba-dd';
    var ul = document.createElement('ul');
    ul.className = 'ba-dd__list';

    groups.forEach(function (g) {
      var li = document.createElement('li');
      li.className = 'ba-dd__group';

      var row = document.createElement('div');
      row.className = 'ba-dd__row';

      var a = document.createElement('a');
      a.className = 'ba-dd__parent';
      a.href = g.url || '#';
      a.textContent = g.title;
      row.appendChild(a);

      if (g.children.length) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ba-dd__toggle';
        btn.setAttribute('aria-expanded', 'false');
        btn.setAttribute('aria-label', 'Show ' + g.title + ' submenu');
        btn.innerHTML = '<span class="ba-dd__caret"></span>';
        row.appendChild(btn);

        var sub = document.createElement('ul');
        sub.className = 'ba-dd__children';
        sub.hidden = true;
        g.children.forEach(function (c) {
          var cli = document.createElement('li');
          var ca = document.createElement('a');
          ca.className = 'ba-dd__child';
          ca.href = c.url || '#';
          ca.textContent = c.title;
          cli.appendChild(ca);
          sub.appendChild(cli);
        });

        btn.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          var open = btn.getAttribute('aria-expanded') === 'true';
          /* one open at a time */
          ul.querySelectorAll('.ba-dd__toggle[aria-expanded="true"]').forEach(function (o) {
            if (o !== btn) { o.setAttribute('aria-expanded', 'false');
              o.closest('.ba-dd__group').querySelector('.ba-dd__children').hidden = true; }
          });
          btn.setAttribute('aria-expanded', open ? 'false' : 'true');
          sub.hidden = open;
        });

        li.appendChild(row);
        li.appendChild(sub);
      } else {
        li.appendChild(row);
      }
      ul.appendChild(li);
    });

    wrap.appendChild(ul);
    return wrap;
  }

  function enhance() {
    document.querySelectorAll('.menu-list__submenu').forEach(function (submenu) {
      if (submenu.hasAttribute(BUILT)) return;
      var inner = submenu.querySelector('.menu-list__submenu-inner');
      if (!inner) return;
      var groups = harvest(submenu);
      if (!groups.length) return;
      submenu.setAttribute(BUILT, '1');
      inner.replaceChildren(build(groups));
    });
  }

  /* ----------------------------------------------------------------------
     Portal the open panel to <body>.

     backdrop-filter is a complete no-op anywhere inside the header:
     blur(200px) renders identically to blur(1px). Something in the header's
     flattened tree establishes a BACKDROP ROOT, so the panel samples an empty
     layer instead of the page behind it. A light-DOM ancestor walk finds
     nothing -- contain, filter, opacity, transform, isolation, mix-blend-mode,
     will-change, mask, perspective, clip-path and container-type were all
     checked on every ancestor up to <html>. Moving this exact element to
     <body> with the exact same value blurs heavily, which is the proof.

     So the panel is lifted out of the header while open and put back on close.
     Inline styles are set with priority 'important' because Horizon's
     visibility rules key off ancestors that no longer match once it has moved.
     ---------------------------------------------------------------------- */
  var DESKTOP = '(min-width: 750px)';
  var FORCED = ['position', 'left', 'top', 'right', 'bottom', 'width', 'height',
                'max-height', 'visibility', 'opacity', 'pointer-events',
                'transform', 'display', 'z-index', 'margin'];

  function scrollTargets() {
    var wrap = document.querySelector('.page-wrapper');
    return wrap ? [wrap, window] : [window];
  }

  function place(li, sub) {
    var r = li.getBoundingClientRect();
    sub.style.setProperty('left', Math.round(r.left) + 'px', 'important');
    sub.style.setProperty('top', Math.round(r.bottom + 6) + 'px', 'important');
  }

  function portalOut(li, sub) {
    if (!window.matchMedia(DESKTOP).matches) return;
    if (sub.__baHome) return;
    sub.__baHome = { parent: sub.parentNode, next: sub.nextSibling, li: li };
    document.body.appendChild(sub);
    sub.classList.add('ba-dd-portal');
    /* Horizon scopes the "Popovers and modals" colours to .color-custom-popover.
       Adding it here lets the panel's background and text follow those theme
       settings instead of a value baked into our CSS. It has to go on at portal
       time and come off at the end, because the class is ours, not Horizon's. */
    sub.classList.add('color-custom-popover');
    sub.style.setProperty('position', 'fixed', 'important');
    sub.style.setProperty('right', 'auto', 'important');
    sub.style.setProperty('bottom', 'auto', 'important');
    sub.style.setProperty('height', 'auto', 'important');
    sub.style.setProperty('max-height', 'none', 'important');
    sub.style.setProperty('visibility', 'visible', 'important');
    sub.style.setProperty('opacity', '1', 'important');
    sub.style.setProperty('pointer-events', 'auto', 'important');
    sub.style.setProperty('transform', 'none', 'important');
    sub.style.setProperty('display', 'block', 'important');
    sub.style.setProperty('margin', '0', 'important');
    sub.style.setProperty('z-index', '1000', 'important');
    place(li, sub);
    sub.__baPlace = function () { place(li, sub); };
    window.addEventListener('resize', sub.__baPlace);
    /* Which element scrolls depends on the viewport -- .page-wrapper at some
       widths, the window at others -- so listen on both. */
    scrollTargets().forEach(function (t) {
      t.addEventListener('scroll', sub.__baPlace, { passive: true });
    });
  }

  function portalBack(sub) {
    if (!sub || !sub.__baHome) return;
    if (sub.__baPlace) {
      window.removeEventListener('resize', sub.__baPlace);
      scrollTargets().forEach(function (t) {
        t.removeEventListener('scroll', sub.__baPlace);
      });
      sub.__baPlace = null;
    }
    FORCED.forEach(function (prop) { sub.style.removeProperty(prop); });
    sub.classList.remove('ba-dd-portal');
    sub.classList.remove('color-custom-popover');
    var home = sub.__baHome;
    sub.__baHome = null;
    /* If Horizon re-rendered the header while the panel was out, its old
       parent is detached -- putting it back there would orphan the node. */
    var parent = home.parent && home.parent.isConnected ? home.parent : home.li;
    if (!parent || !parent.isConnected) { sub.remove(); return; }
    if (home.next && home.next.parentNode === parent) {
      parent.insertBefore(sub, home.next);
    } else {
      parent.appendChild(sub);
    }
  }

  /* ----------------------------------------------------------------------
     Click to open, and stay open.

     Horizon opens submenus on pointerenter via declarative on: bindings and
     keeps them open only while hovered. Intercepting those events in the
     capture phase on document stops its handler running, so open/close
     becomes ours.

     Everything here is DELEGATED to document rather than bound per list item.
     Horizon re-renders the header (section morph, hydration), which swaps the
     <li> nodes out. Per-item listeners silently died with them while the
     one-time guard flag survived on the surviving <ul>, so the rebuilt menu
     ended up with no handlers at all and stopped opening entirely. Delegation
     cannot go stale that way.
     ---------------------------------------------------------------------- */
  var wired = false;

  function isDesktop() { return window.matchMedia(DESKTOP).matches; }

  /* The panel, whether it is still nested or currently portalled out. */
  function panelOf(li) {
    if (li.__baSub && (li.contains(li.__baSub) || li.__baSub.__baHome)) return li.__baSub;
    li.__baSub = li.querySelector('.menu-list__submenu');
    return li.__baSub;
  }

  function closeItem(li) {
    var sub = panelOf(li);

    /* Collapse our own sub-groups first, while we can still reach them --
       once portalled the panel is not a descendant of li.

       This is not just tidiness. Horizon reveals a panel with
         .menu-list__list-item:has([aria-expanded="true"]) > .menu-list__submenu
       and our chevron carries aria-expanded too. Leaving one expanded made the
       closed panel spring back into view the moment it was portalled home, so
       switching menus showed the old panel behind the new one. */
    if (sub) {
      sub.querySelectorAll('[aria-expanded="true"]').forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
        var group = t.closest('.ba-dd__group');
        var kids = group && group.querySelector('.ba-dd__children');
        if (kids) kids.hidden = true;
      });
    }

    li.querySelectorAll('[aria-expanded="true"]').forEach(function (el) {
      el.setAttribute('aria-expanded', 'false');
    });
    delete li.dataset.baOpen;
    if (sub) { portalBack(sub); sub.setAttribute('inert', ''); }
  }

  function closeAll(except) {
    document.querySelectorAll('.menu-list__list-item[data-ba-open]').forEach(function (li) {
      if (li !== except) closeItem(li);
    });
  }

  function openItem(li) {
    li.dataset.baOpen = '1';
    var sub = panelOf(li);
    if (sub) { sub.removeAttribute('inert'); portalOut(li, sub); }
    li.querySelectorAll('.menu-list__disclosure, .menu-list__link').forEach(function (el) {
      if (el.hasAttribute('aria-expanded')) el.setAttribute('aria-expanded', 'true');
    });
  }

  function clickToOpen() {
    if (wired) return;
    wired = true;

    /* Kill hover open/close before Horizon sees it. These events do not bubble,
       but the capture phase still runs from document down to the target. */
    ['pointerenter', 'pointerleave', 'mouseenter', 'mouseleave'].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        if (!isDesktop() || !e.target || !e.target.closest) return;
        var li = e.target.closest('.menu-list__list-item');
        if (li && panelOf(li)) e.stopPropagation();
      }, true);
    });

    document.addEventListener('click', function (e) {
      if (!isDesktop() || !e.target || !e.target.closest) return;

      /* Clicks inside an open panel behave normally. */
      if (e.target.closest('.ba-dd-portal') || e.target.closest('.menu-list__submenu')) return;

      var li = e.target.closest('.menu-list__list-item');
      if (li && !li.matches('[slot="overflow"]') && panelOf(li)) {
        /* The caret is pointer-events:none, so a click on it lands on the item
           rather than the link -- either way the item is what we toggle. */
        e.preventDefault();
        e.stopPropagation();
        var open = li.dataset.baOpen === '1';
        closeAll(li);
        if (open) closeItem(li); else openItem(li);
        return;
      }

      if (!e.target.closest('.menu-list')) closeAll();
    }, true);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAll();
    });
  }

  function boot() { enhance(); clickToOpen(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }
  document.addEventListener('shopify:section:load', boot);
  new MutationObserver(boot).observe(document.documentElement, { childList: true, subtree: true });
})();
